import React, {FC, useMemo} from "react";
import {Table} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {FieldTile, StatusTag} from "../../../Components/ui";
import {operationalPaymentStatusTone} from "../participantOperationsHelpers";
import {sortText, textSorter} from "../tableSorters";
import type {ParticipantRegistration} from "./participantDetailTypes";
import {DetailPanel, EnumTag, FIELD_GRID_STYLE, OfferSummary, useEmptyText} from "./participantDetailUi";
import type {ParticipantDetailModel} from "./useParticipantDetail";

// Current participation and accommodation offers per registration with their payment state.
export const OffersPaymentsSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    const emptyText = useEmptyText();
    const {registrations, primaryRegistration} = model;

    const offerColumns = useMemo<TableColumnsType<ParticipantRegistration>>(() => [
        {
            key: "participationOffer",
            title: t("participation-offer", {defaultValue: "Participation offer"}),
            sorter: textSorter(registration => sortText(
                registration.currentParticipationOfferNumber,
                registration.currentParticipationOfferStatus,
                registration.currentParticipationOfferPaymentStatus,
                registration.participationPaymentMethod
            )),
            render: (_, registration) => (
                <OfferSummary
                    offerNumber={registration.currentParticipationOfferNumber}
                    offerStatus={registration.currentParticipationOfferStatus}
                    paymentStatus={registration.currentParticipationOfferPaymentStatus}
                    paymentMethod={registration.participationPaymentMethod}
                />
            )
        },
        {
            key: "accommodationOffer",
            title: t("accommodation-offer", {defaultValue: "Accommodation offer"}),
            sorter: textSorter(registration => sortText(
                registration.currentAccommodationOfferNumber,
                registration.currentAccommodationOfferStatus,
                registration.currentAccommodationOfferPaymentStatus,
                registration.accommodationPaymentMethod
            )),
            render: (_, registration) => (
                <OfferSummary
                    offerNumber={registration.currentAccommodationOfferNumber}
                    offerStatus={registration.currentAccommodationOfferStatus}
                    paymentStatus={registration.currentAccommodationOfferPaymentStatus}
                    paymentMethod={registration.accommodationPaymentMethod}
                />
            )
        }
    ], [t]);

    const feePaymentStatus = primaryRegistration?.currentParticipationOfferPaymentStatus;
    const accommodationPaymentStatus = primaryRegistration?.currentAccommodationOfferPaymentStatus;

    return (
        <DetailPanel title={t("participant-offers-payments-title", {defaultValue: "Offers and payments"})}>
            <div style={FIELD_GRID_STYLE}>
                <FieldTile
                    label={t("fee-payment", {defaultValue: "Fee payment"})}
                    detail={primaryRegistration?.currentParticipationOfferNumber || undefined}
                >
                    {feePaymentStatus
                        ? <EnumTag value={feePaymentStatus} tone={operationalPaymentStatusTone(feePaymentStatus)}/>
                        : <StatusTag>{t("no-fee-offer", {defaultValue: "None"})}</StatusTag>}
                </FieldTile>
                <FieldTile
                    label={t("accommodation-payment", {defaultValue: "Accommodation payment"})}
                    detail={primaryRegistration?.currentAccommodationOfferNumber || undefined}
                >
                    {accommodationPaymentStatus
                        ? <EnumTag value={accommodationPaymentStatus} tone={operationalPaymentStatusTone(accommodationPaymentStatus)}/>
                        : <StatusTag>{t("no-accommodation-offer", {defaultValue: "None"})}</StatusTag>}
                </FieldTile>
            </div>
            <Table
                size="small"
                rowKey="id"
                columns={offerColumns}
                dataSource={registrations}
                pagination={false}
                scroll={{x: 720}}
                locale={{emptyText}}
            />
        </DetailPanel>
    );
};
